import { Link } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import _ from 'lodash';
import * as React from 'react';
import { Component } from 'react';
import { t } from 'utils/localize';
import ILocation, { ILocationPickerValue } from '../../../../types/Location';
import './LocationPicker.scss';

declare global {
  // tslint:disable-next-line:interface-name
  interface Window {
    resolveGoogleMapsPromise: (google: any) => void;
    google: any;
  }
}

export interface ILocationPickerProps {
  onLocationChange: (pickerLocation: ILocationPickerValue | null) => void;
  location: ILocation | null;
  radius: number | null;
  initialZoom: number;
  maxRadius: number;
  minRadius: number;
  editable: boolean;
  withAutocomplete: boolean;
  error: boolean;
  autofocus?: boolean;
  initialAddress?: string;
  options?: google.maps.MapOptions;
  className?: string | object;
}

export class LocationPicker extends Component<ILocationPickerProps> {
  public static defaultProps = {
    initialZoom: 15,
    maxRadius: 4000,
    minRadius: 100,
    editable: true,
    withAutocomplete: true,
  };

  private defaultLocation = { lat: 37.739941, lng: -122.429213 };
  private defaultRadius = 200;
  private circle: google.maps.Circle | null = null;
  private map: google.maps.Map | null = null;
  private googleMapsPromise: Promise<any> | null = null;
  private listeners: google.maps.MapsEventListener[] = [];

  public componentDidMount() {
    if (window.google) {
      this.setupMap();
    } else {
      this.getGoogleMapPromise().then(() => {
        this.setupMap();
      });
    }
  }

  public componentDidUpdate(prevProps: ILocationPickerProps) {
    const { location, radius } = this.props;

    if (radius) {
      if (this.circle && prevProps.location !== location) {
        if (prevProps.location && !location) {
          this.circle.setCenter(this.defaultLocation);
        } else if (!prevProps.location && location) {
          this.circle.setCenter(location);
        } else if (prevProps.location!.lat !== location!.lat || prevProps.location!.lng !== location!.lng) {
          this.circle.setCenter(location!);
        }
      }
      if (this.circle && prevProps.radius !== radius) {
        const newRadius = !radius ? this.defaultRadius : radius;
        this.circle.setRadius(newRadius);
      }
    } else {
      if (this.circle) {
        this.circle.setCenter(this.defaultLocation);
        this.circle.setRadius(0);
      }
    }
  }

  public handleClear() {
    this.props.onLocationChange(null);
    const input = document.getElementById('places-location-search') as HTMLInputElement;
    input.value = '';
  }

  public componentWillUnmount() {
    _.each(this.listeners, (listener) => listener.remove());
  }

  // Render
  public render() {
    const { withAutocomplete, className, location, initialAddress } = this.props;

    const classes = classNames(
      {
        'location-picker': true,
      },
      className
    );

    const handleKeyPress = async (event: React.KeyboardEvent) => {
      // Enter
      if (event.keyCode === 13) {
        event.stopPropagation();
        event.preventDefault();
        const input = (document.getElementById('places-location-search') as HTMLInputElement).value;

        const geocoder = new google.maps.Geocoder();

        const latlngStr = input.split(',', 2);
        const location = {
          lat: parseFloat(latlngStr[0]),
          lng: parseFloat(latlngStr[1]),
        };
        if (!isNaN(location.lat) && !isNaN(location.lng)) {
          geocoder.geocode(
            { location },
            (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
              if (status === google.maps.GeocoderStatus.OK) {
                if (results && results[0]) {
                  this.circle!.setCenter(location);
                  this.map!.setCenter(location);
                  this.map!.setZoom(this.props.initialZoom);
                  this.props.onLocationChange({ location, locationRadius: this.defaultRadius });
                } else {
                  window.alert('No results found.');
                }
              }
            }
          );
        }
      }
    };

    return (
      <div className={classes}>
        {withAutocomplete && (
          <>
            {!location && initialAddress && (
              <div className="mb-2">
                <Link tag="div" onClick={() => this.setInputToInitialAddress()} href="#" className="mb-4">
                  {t('Search Address')}
                </Link>
              </div>
            )}
            <input
              autoFocus={this.props.autofocus}
              className="location-search mb-2"
              placeholder={t('Search a location or enter coordinates')}
              id="places-location-search"
              onKeyDown={handleKeyPress}
            />
          </>
        )}
        <div className="map-container">
          {!location && (
            <div className="location-picker-overlay">
              {t('Use the search field above to start locating your project')}
            </div>
          )}
          <div className="map" id="map" />
        </div>
        {location && (
          <Link tag="div" href="#" className="pt-3" onClick={() => this.handleClear()}>
            {t('Remove Geolocation')}
          </Link>
        )}
      </div>
    );
  }

  // Load the google maps script if not already loaded
  private getGoogleMapPromise = () => {
    if (!this.googleMapsPromise) {
      this.googleMapsPromise = new Promise((resolve) => {
        window.resolveGoogleMapsPromise = () => {
          resolve(window.google);
          // delete window.resolveGoogleMapsPromise; // TODO: does this need to be deleted?
        };

        const script = document.createElement('script');
        // tslint:disable-next-line:max-line-length
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}&libraries=places&callback=resolveGoogleMapsPromise`;
        script.async = true;
        document.body.appendChild(script);
      });
    }

    return this.googleMapsPromise;
  };

  // Initial map creation and setup
  private setupMap() {
    const { options, location, radius, initialZoom, withAutocomplete } = this.props;
    const initialLocation = location || this.defaultLocation;
    const mapOptions = Object.assign(
      {
        center: initialLocation,
        zoom: initialZoom,
        gestureHandling: 'cooperative',
        streetViewControl: false,
      },
      { ...options }
    );
    this.map = new window.google.maps.Map(document.getElementById('map'), mapOptions);
    if (this.map) {
      const circle = this.createCircleAtLocation(this.map, initialLocation, radius || 0);

      this.map.setCenter(initialLocation);

      this.setMapEventListeners(this.map, circle);
      this.circle = circle;

      if (withAutocomplete) {
        if (window.google.maps.places) {
          const input = document.getElementById('places-location-search') as HTMLInputElement;
          const autocomplete = new window.google.maps.places.Autocomplete(input);
          this.setAutocompleteEventListeners(autocomplete);

          if (!location) {
            this.setInputToInitialAddress();
          }
        }
      }
    }
  }

  private setInputToInitialAddress() {
    const { initialAddress } = this.props;
    const input = document.getElementById('places-location-search') as HTMLInputElement;

    if (initialAddress) {
      input.value = initialAddress;

      setTimeout(() => {
        input.focus();
      }, 200);
    }
  }

  // Set the event listeners for the map and circle
  private setMapEventListeners(map: google.maps.Map, circle: google.maps.Circle) {
    const clickListener = window.google.maps.event.addListener(map, 'click', (event: google.maps.MapMouseEvent) =>
      this.handleMapClick(event, circle)
    );
    const radiusListener = window.google.maps.event.addListener(circle, 'radius_changed', () =>
      this.handleCircleRadiusDidChange(circle)
    );
    const centerListener = window.google.maps.event.addListener(circle, 'center_changed', () =>
      this.handleCircleCenterDidChange(circle)
    );

    this.listeners.push(radiusListener);
    this.listeners.push(centerListener);
    this.listeners.push(clickListener);
  }

  // Set the event listeners for the autocomplete field
  private setAutocompleteEventListeners(autocomplete: google.maps.places.Autocomplete) {
    window.google.maps.event.addListener(autocomplete, 'place_changed', () =>
      this.handleAutocompleteChange(autocomplete)
    );
  }

  // Event handler: Autocomplete field changed
  private handleAutocompleteChange(autocomplete: google.maps.places.Autocomplete) {
    const place = autocomplete.getPlace();
    const location = _.get(place, 'geometry.location');
    if (location) {
      const locationObj = { lat: location.lat(), lng: location.lng() };
      this.circle!.setCenter(locationObj);
      this.map!.setCenter(locationObj);
      this.map!.setZoom(this.props.initialZoom);
      this.props.onLocationChange({ location: locationObj, locationRadius: this.defaultRadius });
    }
  }

  // Event handler: Radius changed
  private handleCircleRadiusDidChange(circle: google.maps.Circle) {
    const { maxRadius, minRadius } = this.props;
    const radius = circle.getRadius();
    if (radius > maxRadius) {
      circle.setRadius(maxRadius);
    } else if (radius < minRadius) {
      circle.setRadius(minRadius);
    }

    this.handleCircleDidChange(circle);
  }

  // Event handler: Location/center changed
  private handleCircleCenterDidChange(circle: google.maps.Circle) {
    this.handleCircleDidChange(circle);
  }

  // Event handler: Map was clicked
  private handleMapClick(event: google.maps.MapMouseEvent, circle: google.maps.Circle) {
    if (event.latLng) {
      circle.setCenter({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    }
  }

  // Call the onLocationChange prop method
  private handleCircleDidChange(circle: google.maps.Circle) {
    const { onLocationChange, location, radius } = this.props;
    const center = circle.getCenter();
    if (center) {
      const lat = center.lat();
      const lng = center.lng();
      const currentRadius = circle.getRadius();
      const locationRadius = currentRadius !== 0 ? circle.getRadius() : this.defaultRadius;

      if (this.props.radius) {
        if (!location || !radius || location.lat !== lat || location.lng !== lng || radius !== locationRadius) {
          onLocationChange({ location: { lat, lng }, locationRadius });
        }
      }
    }
  }

  // Creates a location circle
  private createCircleAtLocation = (map: google.maps.Map, location: ILocation, radius: number) => {
    const newCircle = new window.google.maps.Circle({
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.1,
      map,
      center: location,
      radius,
      editable: this.props.editable,
    });

    return newCircle;
  };
}

export default LocationPicker;
